import React from 'react';
import Header from '../MCAnswerBreakdownReport/components/Header';
import Table from '../MCAnswerBreakdownReport/components/Table';
import { useGetAnswerBreakdownReportMutation } from '../../../../../../features/authentication/reportApiSlice';
import { useAssignmentContext } from '../../../../context';
import { useEffect, useState } from 'react';
import html2pdf from 'html2pdf.js';
import HesSpinner from '../../../../../../components/loadingOverlay/hesSpinner';
import CustomToast from '../../../../../../components/Toast';
import { convertToCSV, downloadCSV, mergeCSVData } from '../../../../utils';

const MCAnswerBreakdownReport = () => {
  const [answerBreakdownReport] = useGetAnswerBreakdownReportMutation();
  const { assignmentId } = useAssignmentContext();
  const [answerBreakdownResponse, setAnswerBreakdownResponse] = useState();
  const [expand, setExpand] = React.useState(false);
  const [loader, setLoader] = React.useState(false);

  const getMCAnswerBreakdownReportAsync = async () => {
    setLoader(true);
    try {
      const options = {
        assignmentId: assignmentId
      };
      const allMCAnswerBreakdownResponse = await answerBreakdownReport(options).unwrap();
      setLoader(false);

      const updatedAnswerBreakdownQuestions = JSON.parse(
        JSON.stringify(allMCAnswerBreakdownResponse.answerBreakdownQuestions)
      );
      updatedAnswerBreakdownQuestions.forEach((question) => {
        const missingResponseIndex = question.questionResponses.findIndex(
          (response) => response.response === 'Missing'
        );

        if (missingResponseIndex !== -1) {
          const missingResponse = question.questionResponses.splice(missingResponseIndex, 1);
          question.questionResponses.push(missingResponse[0]);
        }
      });

      let tempAnswerBreakdownResponse = { ...allMCAnswerBreakdownResponse };
      tempAnswerBreakdownResponse.answerBreakdownQuestions = updatedAnswerBreakdownQuestions;

      setAnswerBreakdownResponse(tempAnswerBreakdownResponse);
    } catch (error) {
      setLoader(false);
      console.log(error);
      CustomToast(error.data, 'error');
    }
  };

  useEffect(() => {
    getMCAnswerBreakdownReportAsync();
  }, []);

  const exportHandler = async () => {
    const newObjectWithNewProperties = {
      Assignment: answerBreakdownResponse.assignmentName || 'Assignment Name',
      Course: answerBreakdownResponse.courseName,
      SchoolName: answerBreakdownResponse.schoolName,
      'Report Date': Date().toLocaleString()
    };

    const responsesList = [];
    const standardsList = [];

    answerBreakdownResponse.answerBreakdownQuestions.forEach((question) => {
      const questionNo = question.questionNo;
      const questionText = question.questionText;
      const questionType = question.questionType;
      const isQuarantined = question.isQuarantined;
      const assignedStudentCount = question.assignedStudentCount;

      question.questionStandards.forEach((standard) => {
        standardsList.push({
          QuestionNo: questionNo,
          StandardName: standard.standardName,
          StandardDescription: standard.standardDescription
        });
      });

      question.questionResponses.forEach((response) => {
        responsesList.push({
          QuestionNo: questionNo,
          QuestionText: questionText,
          QuestionType: questionType,
          IsQuarantined: isQuarantined,
          AssignedStudentCount: assignedStudentCount,
          Response: response.response,
          AnswerText: response.answerText,
          Count: response.count,
          Percentage: (response.count / assignedStudentCount) * 100,
          IsCorrect: response.isCorrect
        });
      });
    });

    const newData = {
      ReportOptions: newObjectWithNewProperties,
      Responses: responsesList,
      Standards: standardsList
    };

    const csvData = convertToCSV(newData);
    const mergedCSV = mergeCSVData(csvData);
    downloadCSV(mergedCSV, 'MCAnswerBreakdownReport');
  };

  const handlePrint = async () => {
    const contentWrapper = document.getElementById('session_pdf_Wrapper');
    const contentClone = contentWrapper.cloneNode(true);
    const options = {
      margin: 40, // Specify the margin for all sides (top, right, bottom, left)
      filename: 'your-pdf-file.pdf', // Specify the desired filename
      image: { type: 'jpeg', quality: 0.98 }, // Set the image type and quality (optional)
      html2canvas: { scale: 2, backgroundColor: null }, // Set the scale for better quality (optional)
      jsPDF: { unit: 'pt', format: 'a3', orientation: 'portrait' }, // Set the document format and orientation (optional)
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] } //Sets Page Break (optional)
    };

    await html2pdf().set(options).from(contentClone).save('MCAnswerBreakdownReport.pdf');
  };

  if (loader) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '40px' }}>
        <HesSpinner />
        <span className="ms-3">Loading results...</span>
      </div>
    );
  }

  return (
    <div className="white-card">
      <Header
        title={'MC Answer Breakdown Report'}
        assignmentName={answerBreakdownResponse?.assignmentName}
        schoolName={answerBreakdownResponse?.schoolName}
        course={answerBreakdownResponse?.courseName}
        handlePrint={handlePrint}
        exportHandler={exportHandler}
      />
      {answerBreakdownResponse?.answerBreakdownQuestions.map((summary, index) => (
        <div key={summary.questionNo}>
          <Table
            title={`Question ${summary.questionNo}`}
            subtitle={
              summary.questionType === 'MC' && summary.isQuarantined === false
                ? `${summary.assignedStudentCount} Students`
                : summary.isQuarantined === true
                  ? 'Quarantined Question'
                  : `Question Type: ${summary.questionType}`
            }
            questionText={summary.questionText}
            setExpand={setExpand}
            expand={expand}
            questionStandards={summary.questionStandards}
            questionResponses={summary.questionResponses}
            studentCount={summary.assignedStudentCount}
            tableShow={summary.studentCount > 0}
            index={index + 1}
            isPrint={false}
            responseCount={summary?.questionResponses?.length}
          />
        </div>
      ))}
      <div className='d-none'>
        <div className="white-card" id="session_pdf_Wrapper" style={{width: 1000, background: '#fff'}}>
          <Header
            title={'MC Answer Breakdown Report'}
            assignmentName={answerBreakdownResponse?.assignmentName}
            schoolName={answerBreakdownResponse?.schoolName}
            course={answerBreakdownResponse?.courseName}
            handlePrint={handlePrint}
            exportHandler={exportHandler}
            isPrint={true}
          />
          {answerBreakdownResponse?.answerBreakdownQuestions.map((summary, index) => (
            <div key={summary.questionNo}>
              <Table
                title={`Question ${summary.questionNo}`}
                subtitle={
                  summary.questionType === 'MC' && summary.isQuarantined === false
                    ? `${summary.assignedStudentCount} Students`
                    : summary.isQuarantined === true
                      ? 'Quarantined Question'
                      : `Question Type: ${summary.questionType}`
                }
                questionText={summary.questionText}
                setExpand={setExpand}
                expand={true}
                questionStandards={summary.questionStandards}
                questionResponses={summary.questionResponses}
                studentCount={summary.assignedStudentCount}
                tableShow={summary.studentCount > 0}
                index={index + 1}
                isPrint={true}
                responseCount={summary?.questionResponses?.length}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MCAnswerBreakdownReport;
